import {
	IconArrowLeft,
	IconArrowRight,
	IconClock,
	IconEye,
	IconTag,
} from "@tabler/icons-react";
import { eq } from "@tanstack/db";
import { useLiveQuery } from "@tanstack/react-db";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { allPosts } from "content-collections";
import { useEffect } from "react";
import { z } from "zod";
import CustomMDX from "@/components/MDX";
import TableOfContents from "@/components/mdx/toc";
import { BlogHeader } from "@/components/misc/BlogHeader";
import ShareLinks from "@/components/share-links";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { config } from "@/data/config";
import {
	blogViewsCollection,
	setBlogViewRow,
} from "@/lib/blog-views/blog-views-collection";
import { getBlogViewCount, recordBlogView } from "@/server/blog-views";
import { schema, toScript } from "@/utils/schema";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/_pages/blog/$path")({
	component: RouteComponent,
	beforeLoad: ({ params }) => {
		try {
			const path = z.string().parse(params.path);
			const post = allPosts.find((p) => p._meta.path === path && p.published);
			if (!post) {
				throw redirect({ to: "/blog" });
			}
			return { post };
		} catch {
			throw redirect({ to: "/blog" });
		}
	},
	loader: async ({ params }) => {
		const path = z.string().parse(params.path);
		const { viewCount } = await getBlogViewCount({ data: { postPath: path } });
		return { initialViewCount: viewCount };
	},
	head: ({ params: { path } }) => {
		const post = allPosts.find((p) => p._meta.path === path);
		const base = seo({
			title: post?.title
				? `${post.title} | ${config.Name}`
				: `Blog | ${config.Name}`,
			description: post?.summary,
			keywords: post?.category,
			tags: post?.tags,
			type: "Article",
			datePublished: post?.date.toISOString(),
			dateModified: post?.date.toISOString(),
			image: post?.image,
			url: `${config.Url}/blog/${path}`,
		});
		return {
			...base,
			scripts: [
				...base.scripts,
				toScript(
					schema.breadcrumb([
						{ name: config.Name, url: config.Url },
						{ name: "Blog", url: `${config.Url}/blog` },
						{
							name: post?.title ?? "Post",
							url: `${config.Url}/blog/${path}`,
						},
					]),
				),
			],
		};
	},
});

function RouteComponent() {
	const { post } = Route.useRouteContext();
	const { initialViewCount } = Route.useLoaderData();
	const url = `${config.Url}/blog/${post?._meta.path}`;
	const postPath = post?._meta.path ?? "";
	const tocItems = post?.tableOfContents ?? [];
	const hasToc = tocItems.length > 0;

	useEffect(() => {
		if (!postPath) return;
		setBlogViewRow(postPath, initialViewCount);
	}, [postPath, initialViewCount]);

	useEffect(() => {
		if (!postPath || typeof sessionStorage === "undefined") return;
		const key = `blogViewRecorded:${postPath}`;
		if (sessionStorage.getItem(key)) return;
		sessionStorage.setItem(key, "1");
		void (async () => {
			try {
				const next = await recordBlogView({ data: { postPath } });
				setBlogViewRow(next.postPath, next.viewCount);
			} catch {
				// View tracking is best-effort only.
			}
		})();
	}, [postPath]);

	const { data: liveRow } = useLiveQuery(
		(q) =>
			q
				.from({ v: blogViewsCollection })
				.where(({ v }) => eq(v.postPath, postPath))
				.findOne(),
		[postPath],
	);

	const displayViews = liveRow?.viewCount ?? initialViewCount;

	return (
		<div className="relative bg-background">
			<div
				className="pointer-events-none absolute inset-0 surface-grid opacity-40"
				aria-hidden
			/>
			<div
				className="pointer-events-none absolute inset-0 bg-linear-to-br from-primary/[0.04] via-transparent to-accent/[0.03] dark:hidden"
				aria-hidden
			/>

			<main className="relative pb-20 pt-12 sm:pt-16">
				<div className="mx-auto max-w-6xl px-4 lg:px-8">
					<div className="mb-8">
						<Button
							variant="ghost"
							size="sm"
							className="-ml-2 rounded-full"
							asChild
						>
							<Link to="/blog">
								<IconArrowLeft className="size-4" />
								All posts
							</Link>
						</Button>
					</div>

					<BlogHeader
						heading={post?.title ?? ""}
						description="By Francis Baker"
						image={post?.image}
						imageAlt={post?.imageAlt ?? post?.title}
						credit={post?.imageCredit}
					/>

					<div className="mb-10 flex flex-wrap items-center gap-2">
						{post?.date ? (
							<span className="inline-flex items-center gap-1.5 rounded-full border border-border/80 bg-muted/20 px-3 py-1 text-sm text-muted-foreground dark:bg-muted/10">
								<IconClock className="size-3.5 text-primary" />
								{new Date(post.date).toLocaleDateString("en-US", {
									year: "numeric",
									month: "long",
									day: "numeric",
								})}
							</span>
						) : null}
						{post?.category ? (
							<span className="inline-flex items-center gap-1.5 rounded-full border border-border/80 bg-muted/20 px-3 py-1 text-sm dark:bg-muted/10">
								<IconTag className="size-3.5 text-primary" />
								<Badge
									variant="secondary"
									className="border-0 bg-transparent px-0 font-normal"
								>
									{post.category}
								</Badge>
							</span>
						) : null}
						{post?.readingTime ? (
							<span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
								{post.readingTime}
							</span>
						) : null}
						<span
							className="inline-flex items-center gap-1.5 rounded-full border border-border/80 bg-muted/20 px-3 py-1 text-sm text-muted-foreground dark:bg-muted/10"
							title="Total views"
						>
							<IconEye className="size-3.5 text-primary" />
							{displayViews.toLocaleString()} views
						</span>
					</div>

					<div className="grid grid-cols-1 gap-10 xl:grid-cols-[minmax(0,1fr)_12.5rem] xl:gap-12 xl:items-start">
						<div className="min-w-0">
							{hasToc ? (
								<div className="mb-8 rounded-xl border border-border/80 bg-muted/15 p-4 dark:bg-muted/10 xl:hidden">
									<TableOfContents items={tocItems} />
								</div>
							) : null}

							<article className="mb-12">
								<div className="rounded-2xl border border-border/80 bg-card/80 p-6 shadow-md dark:bg-card dark:shadow-none sm:p-8 lg:p-10">
									<div className="prose prose-lg max-w-none dark:prose-invert prose-headings:scroll-mt-24 prose-headings:text-foreground prose-a:text-primary prose-pre:m-0 prose-pre:bg-transparent prose-pre:p-0 prose-pre:leading-normal">
										<CustomMDX code={post?.mdx || ""} />
									</div>
								</div>
							</article>

							<div className="rounded-xl border border-border/80 bg-card p-6 shadow-md dark:shadow-none">
								<ShareLinks text={post?.title || ""} url={url} />
							</div>
						</div>

						{hasToc ? (
							<aside className="hidden min-w-0 xl:block">
								<div className="sticky top-28 max-h-[calc(100dvh-8rem)] overflow-y-auto rounded-xl border border-border/80 bg-muted/10 p-4 dark:bg-muted/5">
									<TableOfContents items={tocItems} />
								</div>
							</aside>
						) : null}
					</div>

					<div className="mt-16 rounded-2xl border border-border/80 bg-muted/15 p-8 text-center dark:bg-muted/10 sm:p-10">
						<h2 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
							Enjoyed this article?
						</h2>
						<p className="mx-auto mt-2 max-w-md text-pretty text-muted-foreground">
							More posts on engineering, email, and side projects.
						</p>
						<Button asChild className="mt-6 rounded-full px-6" size="lg">
							<Link to="/blog">
								Browse the blog
								<IconArrowRight className="size-4" />
							</Link>
						</Button>
					</div>
				</div>
			</main>
		</div>
	);
}
